import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import {
  CreateParams,
  CreateResult,
  DataProvider,
  DeleteParams,
  DeleteResult,
  GetListParams,
  GetListResult,
  GetManyReferenceParams,
  GetManyReferenceResult,
  HttpError,
  UpdateParams,
  UpdateResult,
  UpdateManyParams,
  UpdateManyResult,
} from 'react-admin';
import postgraphileDataProvider from 'ra-postgraphile';
import { DataProviderForResources } from './data-provider-for-resources';
import { DelegatingDataProvider } from './delegating-data-provider';
import {
  createClicksCreative,
  createFmaCreative,
  createTargetList,
  duplicateClicksCreative,
  updateClicksCreative,
  updateFmaCreative,
  updateTargetList,
  duplicateFmaCreative,
  duplicateLeadGenCampaign,
  CreateTargetListVariables,
  UpdateTargetListVariables,
  archiveLabel,
  createSponsoredContentTeaser,
  updateSponsoredContentTeaser,
  createEndemicDriver,
  updateEndemicDriver,
  createLeadGenCampaign,
  updateLeadGenCampaign,
  duplicateLineItem,
  withNativeLineItemIdForWrite,
} from './extended-data-provider-methods/mutations';
import {
  fmaCreativesFilterByNativeOrLegacyLineItemId,
  lineItemLinkedCreativesFilterByNativeOrLegacyLineItemId,
  targetListsFilterByLineItemId,
} from './extended-data-provider-methods/queries';
import { ArchiveLabelMutationVariables, ClicksCreative, SponsoredContentTeaser } from 'src/generated/client';
import { isNativeLineItemId } from '../utility/pio-native-lineitem-fk-reads';

const CLICKS_CREATIVES_RESOURCE = 'clicksCreatives';
const SPONSORED_CONTENT_TEASERS_RESOURCE = 'sponsoredContentTeasers';
const LEGACY_LINE_ITEM_TARGET = 'placementsLineItemId';
const RECENT_LINE_ITEMS_CHIP = '_recentLineItemsChip';
const CSADMIN_CREATED_LINE_ITEM_FILTER = { operator: 'isNull', value: true } as const;
const ARCHIVED_AT_ACTIVE_FILTER = { operator: 'isNull', value: true };

type GraphileFilter = Record<string, any>;

const applyArchivedAtActiveFilter = <T extends { filter?: GraphileFilter }>(params: T): T => {
  const { includeArchived, archivedAt: _archivedAt, ...restFilter } = params.filter || {};

  return {
    ...params,
    filter: includeArchived === true ? restFilter : { ...restFilter, archivedAt: ARCHIVED_AT_ACTIVE_FILTER },
  };
};

type BannerTargetingNode = {
  id: number;
  targetListId: number;
  startsAt: string;
  endsAt: string;
  placementsLineItemId: string;
  lineItemId?: string | null;
};

const BANNER_TARGETING_RESOURCES = new Set(['bannerTargeting', 'bannerTargetings']);
const LINE_ITEM_LINKED_DEFAULT_WRITE_RESOURCES = new Set(['bannerTargeting', 'bannerTargetings', 'lineItemLabels']);
// MD-5145: migration 000095 relaxed placements_line_item_id to NULLABLE only on the
// four creative tables. line_item_labels still has it NOT NULL (and as part of its
// primary key), so the native strip must be skipped for it — keep placements_line_item_id.
const PLACEMENTS_LINE_ITEM_ID_REQUIRED_WRITE_RESOURCES = new Set(['lineItemLabels']);

const NATIVE_LINE_ITEM_ID_BY_EXTERNAL_PIO_ID = gql`
  query NativeLineItemIdByExternalPioId($externalPioId: String!) {
    lineItems(condition: { externalPioId: $externalPioId }, first: 1) {
      nodes {
        id
      }
    }
  }
`;

const BANNER_TARGETINGS_BY_DUAL_LINE_ITEM_ID = gql`
  query BannerTargetingsByDualLineItemId(
    $nativeLineItemId: UUID!
    $legacyLineItemId: String!
    $first: Int
    $offset: Int
    $orderBy: [BannerTargetingsOrderBy!]
  ) {
    bannerTargetings(
      first: $first
      offset: $offset
      orderBy: $orderBy
      filter: {
        or: [
          { lineItemId: { equalTo: $nativeLineItemId } }
          { and: [{ lineItemId: { isNull: true } }, { placementsLineItemId: { equalTo: $legacyLineItemId } }] }
        ]
      }
    ) {
      nodes {
        id
        targetListId
        startsAt
        endsAt
        placementsLineItemId
        lineItemId
      }
      totalCount
    }
  }
`;

const toSnakeUpper = (value: string) =>
  value
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();

const bannerTargetingsOrderBy = (sort: GetListParams['sort'] | GetManyReferenceParams['sort']) => {
  if (!sort?.field || !sort?.order) {
    return ['STARTS_AT_DESC'];
  }

  return [`${toSnakeUpper(sort.field)}_${sort.order.toUpperCase()}`];
};

const paginationVariables = (pagination: GetListParams['pagination'] | GetManyReferenceParams['pagination']) => ({
  first: pagination?.perPage,
  offset: pagination ? Math.max(pagination.page - 1, 0) * pagination.perPage : undefined,
});

const filterValueAsString = (value: unknown): string | undefined => {
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }

  if (value && typeof value === 'object') {
    const filterValue = value as { value?: unknown; equalTo?: unknown };
    return filterValueAsString(filterValue.value ?? filterValue.equalTo);
  }

  return undefined;
};

const isBlankFilterValue = (value: unknown): boolean => {
  if (value === '' || value === null || value === undefined) return true;
  if (Array.isArray(value)) return value.length === 0;

  if (value && typeof value === 'object') {
    const filterValue = value as { equalTo?: unknown; value?: unknown };
    if ('equalTo' in filterValue && isBlankFilterValue(filterValue.equalTo)) return true;
    if ('value' in filterValue && isBlankFilterValue(filterValue.value)) return true;
  }

  return false;
};

const stripBlankFilterValues = (filter: GraphileFilter) =>
  Object.fromEntries(Object.entries(filter).filter(([, value]) => !isBlankFilterValue(value)));

const isRecentLineItemsChipActive = (value: unknown) => value === true || value === 'true';

const lineItemResourceFilter = (filter: GraphileFilter | undefined) => {
  const { [RECENT_LINE_ITEMS_CHIP]: recentChip, ...rawRestFilter } = filter || {};
  const restFilter = stripBlankFilterValues(rawRestFilter);
  const scopedFilter = isRecentLineItemsChipActive(recentChip)
    ? { ...restFilter, externalPioId: CSADMIN_CREATED_LINE_ITEM_FILTER }
    : restFilter;

  return applyArchivedAtActiveFilter({ filter: scopedFilter }).filter;
};

const placementsLineItemIdFilter = (params: GetListParams) => filterValueAsString(params.filter?.placementsLineItemId);

const loadNativeLineItemIdForLegacyId = async (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  legacyLineItemId: string,
) => {
  // MD-5145: a native line item's id IS its native UUID. When the incoming read
  // id is already a native UUID (natively-built line item, no externalPioId to
  // translate), use it directly and skip the externalPioId lookup. Mirrors
  // getTargetListLookupLineItemIds in queries.ts.
  if (isNativeLineItemId(legacyLineItemId)) return legacyLineItemId;

  const result = await apolloClient.query<{ lineItems?: { nodes?: Array<{ id: string }> } }>({
    query: NATIVE_LINE_ITEM_ID_BY_EXTERNAL_PIO_ID,
    variables: {
      externalPioId: legacyLineItemId,
    },
    fetchPolicy: 'network-only',
  });

  return result.data.lineItems?.nodes?.[0]?.id;
};

const getBannerTargetingsByDualLineItemRead = async (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  legacyLineItemId: string,
  params: Pick<GetListParams, 'pagination' | 'sort'>,
) => {
  const nativeLineItemId = await loadNativeLineItemIdForLegacyId(apolloClient, legacyLineItemId);

  if (!nativeLineItemId) {
    return undefined;
  }

  const result = await apolloClient.query<{
    bannerTargetings?: { nodes?: BannerTargetingNode[]; totalCount?: number };
  }>({
    query: BANNER_TARGETINGS_BY_DUAL_LINE_ITEM_ID,
    variables: {
      legacyLineItemId,
      nativeLineItemId,
      orderBy: bannerTargetingsOrderBy(params.sort),
      ...paginationVariables(params.pagination),
    },
    fetchPolicy: 'network-only',
  });

  const data = result.data.bannerTargetings?.nodes ?? [];

  return {
    data,
    total: result.data.bannerTargetings?.totalCount ?? data.length,
  };
};

class PostgraphileDataProvider extends DelegatingDataProvider implements DataProviderForResources {
  forResources(): string[] {
    return [
      'advertisers',
      'campaigns',
      'clicks_creatives',
      'endemicDrivers',
      'lineItems',
      'pioUsers',
      'products',
      'specialtyGroups',
    ];
  }
}

export const createPostgraphileDataProvider = async (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  getToken: () => Promise<string | null>,
) => {
  const dataProvider = await postgraphileDataProvider(apolloClient, {
    typeMap: {
      ClicksCreative: {
        expand: true,
      },
      FmaCreative: {
        expand: true,
      },
      TargetList: {
        expand: true,
      },
      SponsoredContentTeaser: {
        expand: true,
      },
      EndemicDriver: {
        expand: true,
      },
      LeadGenCampaign: {
        expand: true,
      },
      Advertiser: {
        expand: true,
        excludeFields: [
          'parent',
          'childAdvertisers',
          'campaigns',
          'campaignsByAgencyId',
          'campaignsByBrandId',
          'campaignsByBillToAdvertiserId',
        ],
      },
      Product: {
        expand: true,
        excludeFields: ['lineItems'],
      },
      PioUser: {
        expand: true,
        excludeFields: ['lineItemsByReviewedBy', 'lineItemDeliveriesByReportedBy'],
      },
    },
  });

  const extendedDataProvider = {
    ...dataProvider,
    getList: async (resource: string, params: GetListParams): Promise<GetListResult<any>> => {
      if (BANNER_TARGETING_RESOURCES.has(resource)) {
        const legacyLineItemId = placementsLineItemIdFilter(params);
        if (legacyLineItemId) {
          const result = await getBannerTargetingsByDualLineItemRead(apolloClient, legacyLineItemId, params);
          if (result) return result;
        }
      }

      // Handle includeArchived filter for targetLists
      if (resource === 'targetLists') {
        // Destructure isArchived to remove any stale filter value from URL params
        const { includeArchived, isArchived: _isArchived, ...restFilter } = params.filter || {};
        // If includeArchived is true, show all (remove archive filter)
        // Otherwise (false, undefined, null), filter to only non-archived
        if (includeArchived === true) {
          params = { ...params, filter: restFilter };
        } else {
          params = {
            ...params,
            filter: { ...restFilter, isArchived: false },
          };
        }
      }
      if (resource === 'campaigns' || resource === 'products') {
        params = applyArchivedAtActiveFilter(params);
      }
      if (resource === 'lineItems') {
        // Strip the recent-7-days quick-chip sentinel; it's UI-only and the
        // backend doesn't know about it. The helper keeps recent queues scoped
        // to CSAdmin-created rows and applies the default active-only filter.
        params = { ...params, filter: lineItemResourceFilter(params.filter) };
      }
      return dataProvider.getList(resource, params);
    },
    create: async (resource: string, params: CreateParams<any>): Promise<CreateResult<any>> => {
      switch (resource) {
        case 'targetLists':
          return createTargetList(apolloClient, params.data as CreateTargetListVariables, getToken);
        case 'clicksCreatives':
          return createClicksCreative(apolloClient, params.data as ClicksCreative);
        case 'fmaCreatives':
          return createFmaCreative(apolloClient, params.data);
        case 'sponsoredContentTeasers':
          return createSponsoredContentTeaser(apolloClient, params.data as SponsoredContentTeaser);
        case 'endemicDrivers':
          return createEndemicDriver(apolloClient, params.data);
        case 'leadGenCampaigns':
          return createLeadGenCampaign(apolloClient, params.data);
        default:
          if (LINE_ITEM_LINKED_DEFAULT_WRITE_RESOURCES.has(resource)) {
            return dataProvider.create(resource, {
              ...params,
              data: await withNativeLineItemIdForWrite(apolloClient, params.data, {
                keepPlacementsLineItemIdForNative: PLACEMENTS_LINE_ITEM_ID_REQUIRED_WRITE_RESOURCES.has(resource),
              }),
            });
          }

          return dataProvider.create(resource, params);
      }
    },
    update: async (resource: string, params: UpdateParams<any>): Promise<UpdateResult<any>> => {
      switch (resource) {
        case 'targetLists':
          return updateTargetList(apolloClient, params.data as UpdateTargetListVariables, getToken);
        case 'clicksCreatives':
          return updateClicksCreative(apolloClient, params.data as ClicksCreative);
        case 'fmaCreatives':
          return updateFmaCreative(apolloClient, params.data);
        case 'archiveLabel':
          return archiveLabel(apolloClient, params.data as ArchiveLabelMutationVariables);
        case 'sponsoredContentTeasers':
          return updateSponsoredContentTeaser(apolloClient, params.data as SponsoredContentTeaser);
        case 'endemicDrivers':
          return updateEndemicDriver(apolloClient, params.data);
        case 'leadGenCampaigns':
          return updateLeadGenCampaign(apolloClient, params.data);
        default:
          if (LINE_ITEM_LINKED_DEFAULT_WRITE_RESOURCES.has(resource)) {
            return dataProvider.update(resource, {
              ...params,
              data: await withNativeLineItemIdForWrite(apolloClient, params.data, {
                clearLineItemIdOnLookupMiss: true,
                keepPlacementsLineItemIdForNative: PLACEMENTS_LINE_ITEM_ID_REQUIRED_WRITE_RESOURCES.has(resource),
              }),
            });
          }

          return dataProvider.update(resource, params);
      }
    },
    getManyReference: async (
      resource: string,
      params: GetManyReferenceParams,
    ): Promise<GetManyReferenceResult<any>> => {
      if (BANNER_TARGETING_RESOURCES.has(resource) && params.target === 'placementsLineItemId') {
        const result = await getBannerTargetingsByDualLineItemRead(apolloClient, String(params.id), params);
        if (result) return result;
      }

      if (resource === 'campaigns') {
        params = applyArchivedAtActiveFilter(params);
      }

      if (resource === 'targetLists' && params.target === 'placementLineItemIdsList') {
        return targetListsFilterByLineItemId(apolloClient, params.id as string);
      }

      if (
        (resource === CLICKS_CREATIVES_RESOURCE || resource === SPONSORED_CONTENT_TEASERS_RESOURCE) &&
        params.target === LEGACY_LINE_ITEM_TARGET
      ) {
        return lineItemLinkedCreativesFilterByNativeOrLegacyLineItemId(apolloClient, resource, params);
      }

      if (resource === 'fmaCreatives' && params.target === LEGACY_LINE_ITEM_TARGET) {
        return fmaCreativesFilterByNativeOrLegacyLineItemId(apolloClient, params);
      }

      if (resource === 'lineItems' && (params.target === 'campaignId' || params.target === 'specialty')) {
        return dataProvider.getManyReference(resource, {
          ...params,
          filter: lineItemResourceFilter(params.filter),
        });
      }

      return dataProvider.getManyReference(resource, params);
    },
    duplicate: async ({ resource, id }: { resource: string; id: number }) => {
      if (resource === 'clicksCreatives') {
        return duplicateClicksCreative(apolloClient, { existingId: id });
      } else if (resource === 'fmaCreatives') {
        return duplicateFmaCreative(apolloClient, {
          existingId: id,
        });
      } else if (resource === 'leadGenCampaigns') {
        return duplicateLeadGenCampaign(apolloClient, {
          existingId: id,
        });
      } else if (resource === 'lineItems') {
        return duplicateLineItem(id, getToken);
      }

      return dataProvider.duplicate ? dataProvider.duplicate({ resource, id }) : undefined;
    },
    updateMany: async (resource: string, params: UpdateManyParams): Promise<UpdateManyResult<any>> => {
      const { ids, data } = params;
      // For target lists, iterate through each ID and call update
      if (resource === 'targetLists') {
        await Promise.all(
          ids.map(id =>
            updateTargetList(apolloClient, { id: id as number, ...data } as UpdateTargetListVariables, getToken),
          ),
        );
        return { data: ids };
      }
      // Fall back to default implementation for other resources
      if (dataProvider.updateMany) {
        return dataProvider.updateMany(resource, params);
      }
      // If no updateMany exists, iterate through updates
      await Promise.all(ids.map(id => dataProvider.update(resource, { id, data, previousData: {} })));
      return { data: ids };
    },
    delete: async (resource: string, params: DeleteParams): Promise<DeleteResult<any>> => {
      const { previousData } = params;
      if (resource === 'clicksCreatives' || resource === 'fmaCreatives') {
        if (previousData && (previousData.isApproved || previousData.hasActivityData)) {
          throw new HttpError(
            'Creative must be changed to Draft status, and must not have activity data in order to be deleted.',
            400,
          );
        }
      } else if (resource === 'targetLists') {
        if (
          previousData &&
          ((previousData?.placementLineItemIdsList && previousData.placementLineItemIdsList.length > 0) ||
            (previousData?.clicksCreativeIdsList && previousData.clicksCreativeIdsList.length > 0) ||
            (previousData?.fmaCreativeIdsList && previousData.fmaCreativeIdsList.length > 0))
        ) {
          throw new HttpError(
            `Target lists cannot be deleted if they are associated with one or more line items or creatives.
            Remove this target list from all associated line items and creatives first.`,
            400,
          );
        }
      }
      return dataProvider.delete(resource, params);
    },
  };

  return new PostgraphileDataProvider(extendedDataProvider);
};
